import { Link } from '@inertiajs/react';
import { useState, useEffect, useCallback } from 'react';
import { ToastContainer } from 'react-toastify';
import {
  services,
  testimonials,
  shopFront,
  blogs,
  shuffleArray,
} from '../utils/Utils';
import Footer from './Footer';
import { IoChevronForwardSharp } from 'react-icons/io5';
import { IoIosArrowBack } from 'react-icons/io';

import hero_logo from '../../frontend/assets/hero_section_logo.svg';
import clientPhoto from '../assets/client_placeholder.svg';
import loadingImg from '../assets/loading.svg';
import workEnv from '../assets/working_env.svg'
import yottanett_services from '../assets/yottanett_services.mp4';
import yottanetCEO from '../assets/yottanet_ceo.png'
import ContactUs from './ContactUs';

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedTestimonials, setDisplayedTestimonials] = useState([]);

  // Function to get a slice of shuffled testimonials
  const getShuffledSlice = useCallback(() => {
    const shuffled = shuffleArray(testimonials);
    // Ensure we don't go out of bounds
    const start = currentIndex % testimonials.length;
    const end = (currentIndex + 2) % testimonials.length;

    if (start < end) {
      setDisplayedTestimonials(shuffled.slice(start, end));
    } else {
      // Handles wrapping around the array
      setDisplayedTestimonials([
        ...shuffled.slice(start),
        ...shuffled.slice(0, end),
      ]);
    }
  }, [currentIndex]);

  useEffect(() => {
    getShuffledSlice();
  }, [getShuffledSlice]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 2) % testimonials.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex - 2;
      return newIndex < 0 ? testimonials.length + newIndex : newIndex;
    });
  };

  return (
    <section className='relative min-h-screen p-4 lg:p-8 overflow-hidden'>
      <div className='flex flex-col lg:flex-row items-center justify-between w-full max-w-7xl lg:mx-auto'>
        <div className='flex-1 max-w-full lg:max-w-2xl text-center lg:text-left z-10 py-10 lg:py-0'>
          <h1 className='text-4xl sm:text-5xl lg:text-6xl font-semibold font-header leading-tight mb-6 tracking-wide'>
            EMPOWERING YOUR <br className='hidden sm:inline' />{' '}
            <span className='text-yellow-primary'>DIGITAL</span> WORLD
          </h1>
          <p className='text-base sm:text-lg font-body text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0'>
            Yottanet Technologies delivers reliable network solutions, expert IT
            consultancy, and cutting-edge technology services for businesses and
            individuals. From seamless connectivity to robust security, we help
            you stay ahead.
          </p>
          <div className='flex flex-col sm:flex-row justify-center lg:justify-start gap-4'>
            <Link
              href='#contact'
              className='bg-yellow-primary hover:bg-yellow-secondary rounded-full text-black font-body font-bold py-3 px-16 shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1'
            >
              Contact us
            </Link>
            <Link
              href='#video'
              preload='false'
              className='flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-white font-body font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1'
            >
              <svg
                className='w-5 h-5 mr-2'
                fill='currentColor'
                viewBox='0 0 20 20'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z'
                  clipRule='evenodd'
                ></path>
              </svg>
              Watch our story
            </Link>
          </div>
        </div>

        <div className='flex-1 flex items-center justify-center p-8 lg:p-0 mt-10 lg:mt-0 z-0'>
          <img
            src={hero_logo}
            alt='hero section logo'
            className='opacity-75 animate-pulse'
          />
        </div>
      </div>

      <div
        id='video'
        className='w-full max-w-7xl bg-[#232121] rounded-xl shadow-xl mt-16 p-8 flex flex-col items-center text-center lg:mx-auto'
      >
        <h2 className='text-3xl sm:text-4xl font-header font-semibold mb-8'>
          DISCOVER WHAT WE DO
        </h2>
        <video width='768' height='360' controls>
          <source src={yottanett_services} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      </div>

      <p className='text-base sm:text-lg text-white font-body font-normal mt-2 max-w-2xl text-center lg:mx-auto'>
        Transforming homes, schools, and businesses{' '}
        <span className='text-gray-400'>
          with innovative IT solutions, professional installations, and
          dedicated support.
        </span>
      </p>

      <section
        className='w-full max-w-full mt-24 p-8 bg-white text-gray-900'
        id='service'
      >
        <div className='max-w-7xl mx-auto'>
          <h2 className='text-4xl font-header font-semibold text-left mb-4'>
            OUR SERVICES
          </h2>
          <div className='flex justify-start mb-8'>
            <span className='bg-blue-100 font-header text-blue-800 text-sm font-semibold px-4 py-1 rounded-full'>
              IT & Technology
            </span>
          </div>
          <p className='text-left font-body font-semibold text-gray-600 text-lg max-w-2xl mb-4'>
            We provide comprehensive IT and Technology solutions that delivers
            tailored services to help businesses optimise for innovative
            performances.
          </p>
          <Link
            href='#contact'
            className='bg-yellow-primary  hover:bg-yellow-secondary rounded-full text-black font-body font-bold py-3 px-8 shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1'
          >
            Contact us
          </Link>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12'>
            {services.map((service) => (
              <div
                key={service?.id}
                className='relative rounded-lg shadow-lg overflow-hidden h-64 flex items-end'
                style={{
                  backgroundImage: `url(${service?.url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className='absolute inset-0 bg-linear-to-t from-black via-black/70 to-transparent'></div>
                <div className='p-6 relative text-white'>
                  <h3 className='text-xl font-header font-bold mb-2'>
                    {service.service}
                  </h3>
                  <p className='text-gray-200 text-sm'>
                    {service?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className='w-full max-w-7xl mx-auto mt-24 p-4 lg:p-8 bg-black text-white'
        id='store'
      >
        <h2 className='text-4xl font-header font-extrabold text-left mb-12'>
          WHAT OUR <br /> CLIENTS SAY
        </h2>{' '}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
          {displayedTestimonials.map((testimony) => (
            <div
              key={testimony?.id}
              className='bg-gray-900 rounded-xl shadow-xl p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6'
            >
              <figure className='shrink-0 w-24 h-24 rounded-full overflow-hidden border-4 border-gray-700'>
                <img
                  src={clientPhoto}
                  alt='Andrew Johnson'
                  className='w-full h-full object-cover'
                />
              </figure>
              <div className='text-center sm:text-left flex-1'>
                {' '}
                <p className='text-yellow-400 text-6xl leading-none font-serif mb-4 -ml-4'>
                  “
                </p>{' '}
                <p className='text-lg font-body text-gray-300 mb-4'>
                  {testimony?.comment}
                </p>
                <p className='font-semibold font-body text-white'>
                  {testimony?.customerName}
                </p>
                <p className='text-sm font-body text-gray-400'>
                  {testimony?.position}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className='flex justify-start items-center gap-4 mt-8 px-2'>
          {/* Carousel Navigation Dots */}
          {testimonials.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full ${
                index >= currentIndex && index < currentIndex + 2
                  ? 'bg-yellow-400'
                  : 'bg-gray-600'
              }`}
            ></div>
          ))}

          <div className='ml-auto flex gap-2'>
            <button
              onClick={handlePrevious}
              className='p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors duration-200'
            >
              <IoIosArrowBack className='w-6 h-6 text-white' />
            </button>
            <button
              onClick={handleNext}
              className='p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors duration-200'
            >
              <IoChevronForwardSharp className='w-6 h-6 text-white' />
            </button>
          </div>
        </div>
      </section>

      <section className='w-full bg-white text-gray-900 mt-24 p-8'>
        {' '}
        <div className='max-w-7xl mx-auto'>
          {' '}
          <h2 className='text-4xl font-header font-semibold text-center mb-12'>
            SHOP QUALITY TECH PRODUCTS
          </h2>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12'>
            {shopFront.map((category) => (
              <div
                key={category?.id}
                className='bg-gray-100 rounded-xl shadow-xl overflow-hidden'
              >
                <img
                  src={category?.background ? category?.background : loadingImg}
                  alt={category?.title}
                  className='w-full h-48 object-cover'
                />
                <div className='p-6'>
                  <h3 className='text-xl font-body font-bold mb-2'>
                    {category?.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
          <div className='flex justify-center'>
            <Link
              href='/products'
              className='bg-yellow-primary mt-2 mb-12 hover:bg-yellow-secondary rounded-full text-black font-body font-bold py-3 px-8 shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1'
            >
              Shop now
            </Link>
          </div>
          <div className='mt-24' id='about'>
            <p className='text-left font-body text-2xl font-bold mb-8'>
              Yottanet Technologies is committed to delivering innovative
              technology solutions which focuses on reliability, customer
              satisfaction and continuous improvement.
            </p>
            <div className='flex flex-col lg:flex-row items-center gap-8'>
              <div className='flex-1'>
                <img
                  src={workEnv}
                  alt='Yottanet Team'
                  className='w-full h-auto rounded-xl shadow-lg object-cover'
                />
              </div>
              <div className='flex-1'>
                <p className='text-gray-700 text-lg mb-4'>
                  Our experienced team serves clients across various industries,
                  ensuring that every project meets the highest standard.
                </p>
                <div className='flex items-center gap-4 mb-8'>
                  <img
                    src={yottanetCEO}
                    alt='ceo'
                    className='w-12 h-12 rounded-full object-cover'
                  />
                  <div>
                    <p className='font-body font-semibold text-gray-800'>
                      Desmond Ayernor
                    </p>
                    <p className='font-body text-sm text-gray-600'>
                      CEO, Network Engineer
                    </p>
                  </div>
                </div>
                <Link
                  href='#contact'
                  className='bg-yellow-primary mt-2 mb-12 hover:bg-yellow-secondary rounded-full text-black font-body font-bold py-3 px-8 shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1'
                >
                  Contact us
                </Link>
              </div>
            </div>
          </div>
          <section className='mt-24' id='blog'>
            <h2 className='font-header text-4xl font-extrabold text-left mb-12'>
              FROM THE <br /> YOTTANET BLOG
            </h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {blogs.map((blog) => (
                <div
                  key={blog?.id}
                  className='bg-gray-100 rounded-xl shadow-xl overflow-hidden'
                >
                  <img
                    src={blog?.cover ? blog?.cover : loadingImg}
                    alt={blog?.title}
                    className='w-full h-48 object-cover'
                  />
                  <div className='p-6'>
                    <h3 className='text-xl font-bold mb-2'>{blog?.title}</h3>
                    <Link
                      href='#'
                      className='font-body shadow-2xl underline-offset-1 text-yellow-primary hover:text-yellow-secondary  font-normal text-sm'
                    >
                      Read
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </section>

      <div className='w-full bg-gray-900 text-white mt-24 p-8 relative overflow-hidden'>
        <div className='absolute inset-0 bg-linear-to-br from-yellow-500/10 via-transparent to-blue-500/10 opacity-75 z-0'></div>

        <section
          className='max-w-7xl mx-auto relative flex flex-col lg:flex-row items-center rounded-xl py-8 md:px-4 bg-gray-900/80 backdrop-blur-sm shadow-2xl'
          id='contact'
        >
          <div className='flex-1 text-center lg:text-left lg:pr-12 mb-10 lg:mb-0'>
            <h2 className='font-header text-4xl sm:text-5xl font-extrabold leading-tight mb-4'>
              READY TO UPGRADE YOUR <br />
              <span className='text-color-yellow-primary'>TECHNOLOGY?</span>
            </h2>
            <p className='font-body text-lg text-gray-300'>
              Contact us today for a free consultation or to request a quote.
            </p>
          </div>

          <div className='flex-1 w-full max-w-md bg-gray-800 rounded-xl p-4 lg:p-8 shadow-xl'>
            <h3 className='font-body text-xl font-bold mb-2'>Contact us</h3>
            <p className='font-body text-sm text-gray-400 mb-6'>
              Provide your details below
            </p>

            <ContactUs />
          </div>
        </section>
      </div>
      <hr className='border-gray-700 my-16 ' />
      <Footer />
      <ToastContainer />
    </section>
  );
};

export default HeroSection;
